import React, { memo, useMemo, useEffect, Fragment } from "react";
import { Row, Col } from "react-bootstrap";
import Chart from "react-apexcharts";
import AOS from "aos";
import "aos/dist/aos.css";

import { useSelector } from "react-redux";
import * as SettingSelector from "../../store/setting/selectors";

import { useDashEkspor } from "./hooks/useDashEkspor";
import { buildFrequensiChart, buildVolumeChart } from "./utils/chartConfig";
import {
  SummaryRow,
  PeriodFilter,
  CommonGroupingFilter,
  ChartCard,
} from "./components/DashEksporComponents";

import ExportBarChart from "./components/custom-bar-chart";
import NilaiExportChart from "./components/nilai-export-chart";

// ─── Color helper ─────────────────────────────────────────────────────────────

const useThemeColors = () => {
  useSelector(SettingSelector.theme_color);
  const prefix = getComputedStyle(document.body).getPropertyValue("--prefix").trim() || "bs-";
  return {
    primary: getComputedStyle(document.body).getPropertyValue(`--${prefix}primary`).trim(),
    info:    getComputedStyle(document.body).getPropertyValue(`--${prefix}info`).trim(),
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

const DashEkspor = memo(() => {
  const {
    // period
    start, end, xAxis, label,
    setPeriodBulan, setPeriodTahun, setPeriodCustom,
    // summary
    jumFreq, jumVol, nilaiIDR, nilaiUSD,
    // charts
    frequensi, bentuk,
    // common grouping
    commonExport, commonKategori, commonOrderBy, commonLimit,
    setCommonKategori, setCommonOrderBy, setCommonLimit,
    // meta
    bulanTahun, tahuns, namaUPT, isVisibleUpt, isTrader,
  } = useDashEkspor();

  const { primary, info } = useThemeColors();
  const colors = [primary, info];

  // Derived current selected labels for dropdowns
  const selectedBulan = start.toLocaleString("id-ID", { month: "long" }) + " " + start.getFullYear();
  const selectedTahun = String(start.getFullYear());

  // Chart configs (memoized)
  const chartFrequensi = useMemo(() => buildFrequensiChart(frequensi, xAxis, colors), [frequensi, xAxis, primary, info]);
  const chartVolume    = useMemo(() => buildVolumeChart(frequensi, xAxis, colors),    [frequensi, xAxis, primary, info]);

  useEffect(() => {
    AOS.init({ startEvent: "DOMContentLoaded", once: true, duration: 700, offset: 10,
      disable: () => window.innerWidth < 996 });
  }, []);

  // ── Trader view ────────────────────────────────────────────────────────────
  if (isTrader) {
    return (
      <Row>
        <Col md="12">
          <div className="d-flex justify-content-between flex-wrap iq-navbar-header mb-4">
            <div>
              <h1 className="text-white">SIAP MUTU</h1>
              <h3 className="text-light">Sistem Informasi Aplikasi Pelayanan Mutu</h3>
            </div>
          </div>
        </Col>
      </Row>
    );
  }

  // ── Main view ──────────────────────────────────────────────────────────────
  return (
    <Fragment>
      {/* Header & Filter */}
      <Row style={{ position: "relative", top: "-20px" }}>
        <Col md="12">
          <div className="iq-navbar-header mb-4">
            <Row className="align-items-center mt-1">
              {/* Judul */}
              <Col lg={8} md={12} className="mb-3 mb-lg-0">
                <h2 className="text-white fw-bold mb-0">Ekspor Hasil Kelautan dan Perikanan</h2>
                <h2 className="text-white mb-0">{isVisibleUpt ? namaUPT : "BPPMHKP"}</h2>
                <h3 className="text-light">{label}</h3>
              </Col>

              {/* Filter periode */}
              <PeriodFilter
                start={start} end={end}
                tahuns={tahuns}
                SelectedTahun={selectedTahun}
                onTahun={setPeriodTahun}
                onCustomApply={setPeriodCustom}
              />
            </Row>
          </div>
        </Col>
      </Row>

      {/* Summary cards */}
      <SummaryRow jumFreq={jumFreq} jumVol={jumVol} nilaiIDR={nilaiIDR} nilaiUSD={nilaiUSD} />

      {/* Charts */}
      <Row>
        <Col md="12" lg="6">
          <Row>
            <ChartCard title="Grafik Frekuensi Ekspor">
              {chartFrequensi && (
                <Chart options={chartFrequensi.options} series={chartFrequensi.series} type="area" height="245" />
              )}
            </ChartCard>
          </Row>
        </Col>

        <Col md="12" lg="6">
          <Row>
            <ChartCard title="Grafik Volume Ekspor">
              {chartVolume && (
                <Chart options={chartVolume.options} series={chartVolume.series} type="bar" height="245" />
              )}
            </ChartCard>
          </Row>
        </Col>

        <Col md="12">
          <Row>
            <ChartCard title="Grafik Nilai Ekspor">
              <NilaiExportChart data={frequensi} xAxis={xAxis} colors={["#F59E0B", "#3B82F6"]} />
            </ChartCard>
          </Row>
        </Col>

        <Col md="12">
          <Row>
            <ChartCard
              header={
                <CommonGroupingFilter
                  commonKategori={commonKategori} setCommonKategori={setCommonKategori}
                  commonOrderBy={commonOrderBy}   setCommonOrderBy={setCommonOrderBy}
                  commonLimit={commonLimit}        setCommonLimit={setCommonLimit}
                />
              }
            >
              <ExportBarChart
                data={commonExport}
                kategori={commonKategori}
                orderBy={commonOrderBy}
                limit={commonLimit}
              />
            </ChartCard>
          </Row>
        </Col>
      </Row>
    </Fragment>
  );
});

export default DashEkspor;
